import React, { useState, useEffect } from 'react';
import { Plus, Users, Briefcase, DollarSign, ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import TeacherForm from '../components/teachers/TeacherForm';
import TeacherList from '../components/teachers/TeacherList';
import { ConfirmDialog } from '../components/ui';
import { teachersApi, dashboardApi } from '../services/api';
import { Teacher, TeacherFormData, DashboardStats } from '../types';

const Teachers: React.FC = () => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teachersData, statsData] = await Promise.all([
        teachersApi.getAll(),
        dashboardApi.getStats()
      ]);
      setTeachers(teachersData);
      setStats(statsData);
    } catch (error) {
      toast.error('Failed to load teacher data');
      console.error('Error loading teacher data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeacher = async (data: TeacherFormData) => {
    try {
      setSubmitting(true);
      const newTeacher = await teachersApi.create(data);
      setTeachers(prev => [newTeacher, ...prev]);
      setShowForm(false);
      toast.success('Teacher added successfully');
    } catch (error) {
      toast.error('Failed to add teacher');
      console.error('Error creating teacher:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTeacher = async (data: TeacherFormData) => {
    if (!editingTeacher) return;

    try {
      setSubmitting(true);
      const updatedTeacher = await teachersApi.update(editingTeacher.id, data);
      setTeachers(prev =>
        prev.map(teacher =>
          teacher.id === editingTeacher.id ? updatedTeacher : teacher
        )
      );
      setEditingTeacher(null);
      setShowForm(false);
      toast.success('Teacher updated successfully');
    } catch (error) {
      toast.error('Failed to update teacher');
      console.error('Error updating teacher:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTeacher = (teacher: Teacher) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Teacher',
      message: `Are you sure you want to delete "${teacher.name}"?\n\nThis action cannot be undone and will remove the teacher record permanently.`,
      onConfirm: async () => {
        try {
          await teachersApi.delete(teacher.id);
          setTeachers(prev => prev.filter(t => t.id !== teacher.id));
          toast.success('Teacher deleted successfully');
        } catch (error) {
          toast.error('Failed to delete teacher');
          console.error('Error deleting teacher:', error);
        }
      }
    });
  };

  const handleViewTeacher = (teacher: Teacher) => {
    // For now, we'll use the edit form in view mode
    setEditingTeacher(teacher);
    setShowForm(true);
  };

  const handleEdit = (teacher: Teacher) => {
    setEditingTeacher(teacher);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingTeacher(null);
  };

  // Calculate real-time stats from teacher data
  const realTimeStats = {
    totalTeachers: teachers.length,
    activeTeachers: teachers.filter(t => t.status === 'Active').length,
    monthlySalary: teachers.filter(t => t.status === 'Active').reduce((sum, t) => sum + t.salary, 0)
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <div>
          <button
            onClick={handleCancelForm}
            className="flex items-center text-sm text-[#b9ad9b] hover:text-[#f3ede2] transition-colors mb-3"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to Teacher Management
          </button>
          <h1 className="text-2xl font-bold text-[#f3ede2]">
            {editingTeacher ? 'Edit Teacher' : 'Add New Teacher'}
          </h1>
          <p className="text-[#b9ad9b] mt-1">
            {editingTeacher ? 'Update teacher information' : 'Add a new teacher to the school'}
          </p>
        </div>

        <TeacherForm
          onSubmit={editingTeacher ? handleEditTeacher : handleCreateTeacher}
          loading={submitting}
          onCancel={handleCancelForm}
          initialData={editingTeacher ? {
            name: editingTeacher.name,
            email: editingTeacher.email,
            phone: editingTeacher.phone,
            aadharNumber: editingTeacher.aadharNumber,
            subject: editingTeacher.subject,
            department: editingTeacher.department,
            qualification: editingTeacher.qualification,
            dateOfJoining: editingTeacher.dateOfJoining,
            salary: editingTeacher.salary,
            bankDetails: editingTeacher.bankDetails
          } : undefined}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#f3ede2]">Teacher Management</h1>
          <p className="text-[#b9ad9b] mt-1">Manage teaching staff and salary payments</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Teacher</span>
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-[#211c15] rounded-lg border border-[#3a3122] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#b9ad9b] text-sm font-medium">Total Teachers</p>
              <p className="text-2xl font-bold text-[#f3ede2] mt-2">{realTimeStats.totalTeachers}</p>
            </div>
            <div className="w-12 h-12 bg-[#173322] rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-[#4ade80]" />
            </div>
          </div>
        </div>

        <div className="bg-[#211c15] rounded-lg border border-[#3a3122] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#b9ad9b] text-sm font-medium">Active Teachers</p>
              <p className="text-2xl font-bold text-[#f3ede2] mt-2">{realTimeStats.activeTeachers}</p>
            </div>
            <div className="w-12 h-12 bg-[#16212b] rounded-lg flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-[#60a5fa]" />
            </div>
          </div>
        </div>

        <div className="bg-[#211c15] rounded-lg border border-[#3a3122] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#b9ad9b] text-sm font-medium">Monthly Salary</p>
              <p className="text-2xl font-bold text-[#f3ede2] mt-2">₹{realTimeStats.monthlySalary.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-[#332711] rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-[#f0b64c]" />
            </div>
          </div>
        </div>
      </div>

      {/* Teachers List */}
      <TeacherList
        teachers={teachers}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDeleteTeacher}
        onView={handleViewTeacher}
      />

      {/* Dialog Components */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type="danger"
      />
    </div>
  );
};

export default Teachers;